





























































































import {
    defineComponent,
    reactive,
    toRefs,
    ref,
    Ref,
    computed,
    PropType,
    watch,
} from '@vue/composition-api';
import Vue from 'vue';
import { assign, cloneDeep } from 'lodash-es';
import DataSet from '@antv/data-set';
import { ChartType, LinkageParamType, ChartSeriesType } from '../type';
import chartTop from './chart-top.vue';
import {
    getReportSeries,
    formatDimension,
    getStringfyParams,
} from '../service';
import {
    TooltipHtmlContent,
    MixLineDataLabelConfig,
    MixBarDataLabelConfig,
    CommonChartConfig,
    GameChartConfig,
    DimensionFormatMap,
} from '../const';
import { format, intervalToDuration } from 'date-fns';

export default defineComponent({
    components: {
        chartTop,
    },
    props: {
        chart: {
            type: Object as PropType<ChartType>,
            required: true,
        },
        linkage: {
            type: Object as PropType<LinkageParamType>,
            required: true,
        },
        appId: {
            type: String,
            required: true,
        },
        timeLabel: {
            type: String,
            required: true,
        },
        chartWidth: {
            type: Number,
            default: 1216,
        },
        groupName: {
            type: String,
            required: true,
        },
        isGameTab: {
            type: Boolean,
            default: false,
        },
    },
    setup(props, context) {
        const chartData: Ref<Array<Record<string, any>>> = ref([]);
        const lineSeriesNames: Ref<Array<string>> = ref([]);
        const barSeriesNames: Ref<Array<string>> = ref([]);
        const seriesNames: Ref<Array<string>> = ref([]);
        const lineSeriesCaptions: Ref<Array<string>> = ref([]);
        const barSeriesCaptions: Ref<Array<string>> = ref([]);
        const seriesCaptions: Ref<Array<string>> = ref([]);
        const mixLineDataLabelConfig: Ref<Array<any>> = ref([]);
        const mixBarDataLabelConfig: Ref<Array<any>> = ref([]);
        const renderComponent = ref(true);
        const timeShowType = ref('day');
        let originData: Array<Record<string, any>> = [];
        const tooltipHtmlContent: Ref<any> = ref({});
        const isCheckLegends: Ref<Array<string>> = ref([]);
        const isLoading = ref(false);
        let isRendering = false;
        const dimensionFormatType = computed(() => {
            return DimensionFormatMap[
                timeShowType.value as keyof typeof DimensionFormatMap
            ].type;
        });
        const dimensionType = computed(() => {
            return DimensionFormatMap[
                timeShowType.value as keyof typeof DimensionFormatMap
            ].dimensionType;
        });
        let config: Record<string, any> = props.isGameTab
            ? cloneDeep(GameChartConfig)
            : cloneDeep(CommonChartConfig);
        config = assign({}, config, props.chart.style);
        config.scale[1].formatter = function formatter(text: number) {
            return +text >= 10000
                ? (+text - (+text % 1000)) / 10000 + 'w'
                : text;
        };
        const barColors = cloneDeep(config.barColor[1]);
        const mixChartColors = [...config.barColor[1], config.lineColor[1]];

        function transformMixData(sourceData: Array<Record<string, any>>) {
            let lineSource: Array<Record<string, any>> = [];
            let barSource: Array<Record<string, any>> = [];
            sourceData.forEach((item: Record<string, any>) => {
                const lineItem: Record<string, any> = {};
                const barItem: Record<string, any> = {};
                lineItem.dimension = item.dimension;
                barItem.dimension = item.dimension;
                lineSeriesNames.value.forEach((e: string) => {
                    lineItem[e] = item[e];
                });
                barSeriesNames.value.forEach((e: string) => {
                    barItem[e] = item[e];
                });
                lineSource.push(lineItem);
                barSource.push(barItem);
            });
            lineSource = new DataSet.View().source(lineSource).transform({
                type: 'fold',
                fields: lineSeriesNames.value,
                key: 'lineChartKey',
                value: 'lineChartValue',
            }).rows;
            barSource = new DataSet.View().source(barSource).transform({
                type: 'fold',
                fields: barSeriesNames.value,
                key: 'barChartKey',
                value: 'barChartValue',
            }).rows;
            barSource.forEach((e) => {
                const item: Record<string, any> = lineSource.find(
                    (x) => x.dimension === e.dimension,
                )!;
                e.lineChartKey = item.lineChartKey;
                e.lineChartValue = item.lineChartValue;
            });
            return barSource;
        }

        let chartSourceData: Array<Record<string, any>> = [];
        function transformTimeType(result: Array<Record<string, any>>) {
            chartSourceData = transformMixData(
                formatDimension(dimensionFormatType.value, cloneDeep(result)),
            );
            chartData.value = chartSourceData;
        }

        async function getChartData() {
            isLoading.value = true;
            try {
                const linkageParams = {};
                Object.keys(props.linkage).forEach((item) => {
                    if (item !== 'dateRange') {
                        // @ts-ignore
                        linkageParams[item] = props.linkage[item].join(',');
                    }
                });
                const res = await getReportSeries(
                    {
                        source: props.chart.source,
                        appId: props.appId,
                        dimensionType: dimensionType.value,
                        dateRange: props.linkage.dateRange,
                        ...linkageParams,
                    },
                    props.isGameTab,
                );
                const series = res.series as ChartSeriesType[];
                if (!seriesNames.value.length) {
                    lineSeriesNames.value = series
                        .filter((item) => item.type === 'line')
                        .map((item) => item.seriesName);
                    barSeriesNames.value = series
                        .filter((item) => item.type === 'bar')
                        .map((item) => item.seriesName);
                    seriesNames.value = [
                        ...barSeriesNames.value,
                        ...lineSeriesNames.value,
                    ];

                    lineSeriesCaptions.value = series
                        .filter((item) => item.type === 'line')
                        .map((item) => item.seriesCaption);
                    barSeriesCaptions.value = series
                        .filter((item) => item.type === 'bar')
                        .map((item) => item.seriesCaption);
                    seriesCaptions.value = [
                        ...barSeriesCaptions.value,
                        ...lineSeriesCaptions.value,
                    ];
                    tooltipHtmlContent.value = TooltipHtmlContent(series);
                    isCheckLegends.value = cloneDeep(seriesNames.value);
                }

                if (res.result !== 1 || !res.data.length) {
                    chartData.value = [];
                    return;
                }
                config.scale[1].min = res.y_axis_min;
                // @ts-ignore
                config.scale[1].max = Math.ceil(res.y_axis_max);
                if (res.y_axis_min === 0) {
                    // 防止纵坐标最小刻度为负数
                    config.scale[1].max = 10;
                }
                if (res.y_axis_max <= 1000000) {
                    config.padding[3] = 40;
                }
                config.barColor[1] = cloneDeep(barColors);
                originData = cloneDeep(res.data);
                // eslint-disable-next-line no-use-before-define
                updateChartData();
            } catch (e) {
                originData = [];
                chartData.value = [];
            } finally {
                isLoading.value = false;
            }
        }

        function forceUpdateComp() {
            renderComponent.value = false;
            setTimeout(() => {
                renderComponent.value = true;
                isRendering = false;
            }, 0);
        }

        function showDataLabel(isShow: boolean) {
            if (!isShow) {
                mixLineDataLabelConfig.value = [];
                mixBarDataLabelConfig.value = [];
                forceUpdateComp();
                return;
            }
            mixLineDataLabelConfig.value = MixLineDataLabelConfig;
            mixBarDataLabelConfig.value = MixBarDataLabelConfig;
            forceUpdateComp();
        }
        async function changeShowWay(way: string) {
            isCheckLegends.value = cloneDeep(seriesNames.value);
            await getChartData();
        }

        function updateChartData() {
            const data = cloneDeep(originData);
            if (!data.length) {
                chartData.value = [];
                return;
            }
            isCheckLegends.value = cloneDeep(seriesNames.value);
            transformTimeType(data);
        }
        function legendOnClick(value: string) {
            if (isRendering) {
                return;
            }
            isRendering = true;
            const itemIndex = isCheckLegends.value.indexOf(value);
            const isNotChecked = itemIndex !== -1; // 按钮置灰
            const isLineLegend = lineSeriesNames.value.indexOf(value) !== -1;
            if (isCheckLegends.value.length === 1 && isNotChecked) {
                isRendering = false;
                return;
            }
            chartData.value = chartSourceData;
            let temp = cloneDeep(chartData.value);
            isNotChecked
                ? isCheckLegends.value.splice(itemIndex, 1)
                : isCheckLegends.value.push(value);

            if (isLineLegend) {
                if (isNotChecked) {
                    temp.forEach((item) => {
                        delete item.lineChartKey;
                        delete item.lineChartValue;
                    });
                }
            }
            if (
                !isCheckLegends.value.filter((v) => {
                    return barSeriesNames.value.indexOf(v) > -1;
                }).length
            ) {
                temp.forEach((item) => {
                    delete item.barChartKey;
                    delete item.barChartValue;
                });
            } else {
                temp = new DataSet()
                    .createView()
                    .source(temp)
                    .transform({
                        type: 'filter',
                        callback: (obj) => {
                            return isCheckLegends.value.includes(
                                obj.barChartKey,
                            );
                        },
                    }).rows;
                if (!isCheckLegends.value.includes(lineSeriesNames.value[0])) {
                    temp.forEach((item) => {
                        delete item.lineChartKey;
                        delete item.lineChartValue;
                    });
                }
            }
            config.barColor = ['barChartKey', []];
            barSeriesNames.value.forEach((item: string, index: number) => {
                if (isCheckLegends.value.indexOf(item) > -1) {
                    config.barColor[1].push(barColors[index]);
                }
            });
            chartData.value = temp;
            forceUpdateComp();
        }

        function refreshTimeOptions() {
            const interval = intervalToDuration({
                start: props.linkage.dateRange[0],
                end: props.linkage.dateRange[1],
            });
            if (
                !props.chart.dimensionUnion &&
                props.chart.dimensionUnion !== undefined
            ) {
                timeShowType.value = 'day';
                return;
            }
            if (props.timeLabel === 'year' || interval.years! > 0) {
                timeShowType.value = 'month';
            } else if (props.timeLabel === 'quarter' || interval.months! > 2) {
                timeShowType.value = 'month';
            } else {
                timeShowType.value = 'day';
            }
        }

        function downloadExcel() {
            const a = document.createElement('a');
            a.href =
                `https://${window.location.host}/portal/data/report/download?appId=${props.appId}&source=${props.chart.source}` +
                `&dateRangeStart=${props.linkage.dateRange[0]}&dateRangeEnd=${props.linkage.dateRange[1]}` +
                `&chartCaption=${props.chart.chartCaption}&groupCaption=${props.groupName}` +
                `&${getStringfyParams(
                    cloneDeep(props.linkage),
                    dimensionType.value,
                )}`;
            a.click();
        }

        watch(
            () => props.linkage,
            async (v) => {
                if (props.appId && v) {
                    refreshTimeOptions();
                    mixLineDataLabelConfig.value = [];
                    mixBarDataLabelConfig.value = [];
                    await getChartData();
                }
            },
            { immediate: true, deep: true },
        );

        return {
            isLoading,
            showDataLabel,
            changeShowWay,
            chartData,
            config,
            tooltipHtmlContent,
            mixLineDataLabelConfig,
            mixBarDataLabelConfig,
            renderComponent,
            seriesNames,
            seriesCaptions,
            updateChartData,
            legendOnClick,
            isCheckLegends,
            timeShowType,
            downloadExcel,
            mixChartColors,
        };
    },
});
